import { createContext, useContext, useState } from 'react';
import { MelidataTrack } from '../../../../../services/Track';
import { usePage, type PageContextType } from '../../../../contexts/PageContext';
import type {
  StepInfo,
  RegulatedStepData,
  RegulatedModalContent,
  RegulatedStepValidation,
} from '../../../../../services/types';
import { getNextStep } from '../../../../helpers/getNextStep';

interface Props {
    children: JSX.Element[] | JSX.Element,
    steps?: StepInfo[],
    registrationID?: string,
    urlToDeclaredEmail?: string,
    editDeclaredEmailTrack: MelidataTrack,
    parityFlow?: boolean,
}

export interface StepsContextProps {
    steps?: StepInfo[] | undefined,
    registrationID?: string | undefined,
    urlToDeclaredEmail?: string | undefined,
    isEditableStep?(stepID: string): boolean,
    editDeclaredEmailTrack: MelidataTrack,
    isRegulatedStep({ stepId, uri, isMobile }: RegulatedStepValidation): boolean,
    regulatedStepData: RegulatedStepData,
}

const regulatedSteps = [
  {
    platformId: 'MP',
    siteId: 'MLM',
    stepId: 'tyc',
    content: 'MLM_WALLET' as RegulatedModalContent,
  },
];

const StepsContext = createContext<StepsContextProps | undefined>(undefined);

const StepsProvider = (props: Props): JSX.Element => {
  const { steps, registrationID, urlToDeclaredEmail, editDeclaredEmailTrack, parityFlow = false, children } = props;
  const { platformId, siteId } = usePage() as PageContextType;
  const [regulatedStepData, setRegulatedStepData] = useState<RegulatedStepData>();

  const isEditableStep = (stepID: string) => (stepID === 'declared_email' && getNextStep(steps!, stepID)?.status === 'pending') && !parityFlow;

  const isRegulatedStep = ({ stepId, uri, isMobile }: RegulatedStepValidation) => {
    const regulatedStep = regulatedSteps.find((workflow) => workflow.platformId === platformId
        && workflow.siteId === siteId
        && workflow.stepId === stepId
        && uri
        && !isMobile);

    if (regulatedStep) {
      setRegulatedStepData({
        ...regulatedStep,
        uri,
      });
      return true;
    }
    return false;
  };

  return (
    <StepsContext.Provider value={{
      steps,
      registrationID,
      urlToDeclaredEmail,
      isEditableStep,
      isRegulatedStep,
      editDeclaredEmailTrack,
      regulatedStepData,
    }}
    >
      {children}
    </StepsContext.Provider>
  );
};

const useStepsContext = (): StepsContextProps | undefined => useContext(StepsContext);

export {
  useStepsContext,
  StepsProvider,
};

